import { Alert, Stack, TextInput } from '@mantine/core'
import { Button, H3 } from '@packages/ui'
import { useTranslation } from 'react-i18next'
import { useForgotPassword } from './hooks/useForgotPassword'
import { renderSlot } from './renderSlot'
import type { ForgotPasswordContext, ForgotPasswordFormProps } from './types'

function ForgotPasswordFormBody({
  ctx,
  onBackToSignIn,
  slots,
  classNames
}: Omit<ForgotPasswordFormProps, 'forgot' | 'forgotOptions'> & {
  ctx: ForgotPasswordContext
}) {
  const { t } = useTranslation()
  const { state, actions } = ctx
  const { email, loading, error, resetEmailSent } = state

  const inner = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void actions.submit()
      }}
    >
      <Stack gap="md" className={classNames?.panel}>
        {renderSlot(
          slots?.heading,
          () => (
            <H3 data-testid="forgot-password-heading">
              {t('auth.forgotten_password.label', {
                defaultValue: 'Recover password'
              })}
            </H3>
          ),
          ctx
        )}

        {renderSlot(
          slots?.instructions,
          () => (
            <p className={classNames?.instructions}>
              {t('auth.forgot_password.instructions', {
                defaultValue:
                  "Enter your email and we'll send you a link to reset your password."
              })}
            </p>
          ),
          ctx
        )}

        {renderSlot(
          slots?.emailInput,
          () => (
            <TextInput
              data-testid="password-email"
              label={t('auth.email.label', { defaultValue: 'Email' })}
              placeholder={t('auth.email.placeholder', {
                defaultValue: 'email@example.com'
              })}
              type="email"
              value={email}
              onChange={(e) => actions.setEmail(e.target.value)}
              required
            />
          ),
          ctx
        )}

        {renderSlot(
          slots?.error,
          () =>
            error ? (
              <Alert color="red" variant="light" data-testid="password-error">
                {error}
              </Alert>
            ) : null,
          ctx
        )}

        {renderSlot(
          slots?.success,
          () =>
            resetEmailSent ? (
              <Alert color="green" variant="light" data-testid="password-success">
                {t('auth.reset_password_email_sent.label', {
                  defaultValue:
                    'If an account exists with this email, you will receive a password reset link.'
                })}
              </Alert>
            ) : null,
          ctx
        )}

        {!resetEmailSent &&
          renderSlot(
            slots?.submit,
            () => (
              <Button
                loading={loading}
                data-testid="send-password-reset-button"
                type="submit"
                onClick={() => void actions.submit()}
              >
                {t('auth.send_reset_email.label', {
                  defaultValue: 'Send Password Reset Email'
                })}
              </Button>
            ),
            ctx
          )}

        {renderSlot(
          slots?.back,
          () => (
            <Button
              variant={resetEmailSent ? 'primary' : 'transparent'}
              size="xs"
              data-testid="to-signin-button"
              type="button"
              onClick={onBackToSignIn}
            >
              {t('auth.back_to_login.label', { defaultValue: 'Back to login' })}
            </Button>
          ),
          ctx
        )}
      </Stack>
    </form>
  )

  return slots?.panel ? <>{slots.panel(inner)}</> : inner
}

export const ForgotPasswordForm = ({
  forgot: forgotProp,
  forgotOptions,
  onBackToSignIn,
  slots,
  classNames
}: ForgotPasswordFormProps) => {
  if (!forgotOptions && !forgotProp) {
    throw new Error('ForgotPasswordForm requires `forgot` or `forgotOptions`')
  }

  const internalForgot = useForgotPassword(
    forgotOptions ?? {
      email: '',
      setEmail: () => undefined
    }
  )
  const ctx = forgotProp ?? internalForgot

  return (
    <ForgotPasswordFormBody
      ctx={ctx}
      onBackToSignIn={onBackToSignIn}
      slots={slots}
      classNames={classNames}
    />
  )
}
